import React, { useState, useEffect } from 'react'
import Swal from 'sweetalert2'
import apiClient, { BASE_URL } from '../../config/api'

const ExchangeRate = () => {
  const [currentRate, setCurrentRate] = useState('')
  const [loading, setLoading] = useState(false)
  const [fetching, setFetching] = useState(false)
  const [rateExists, setRateExists] = useState(false)

  useEffect(() => {
    fetchExchangeRate()
  }, [])

  const fetchExchangeRate = async () => {
    setFetching(true)
    try {
      const response = await apiClient.get(`${BASE_URL}/api/sales/exchange-rate/`)
      setCurrentRate(response.data?.current_rate || response.data?.rate || '')
      setRateExists(true)
    } catch (error) {
      console.error('Error fetching exchange rate:', error)
      const errorMessage = error.response?.data?.detail ||
                          error.response?.data?.message ||
                          error.response?.data?.error
      
      // If exchange rate doesn't exist, that's okay - we'll create it
      if (errorMessage && errorMessage.includes('not exist')) {
        setRateExists(false)
        setCurrentRate('')
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: errorMessage || 'Failed to fetch exchange rate. Please try again.'
        })
      }
    } finally {
      setFetching(false)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)

    try {
      const rateValue = parseFloat(currentRate)
      if (isNaN(rateValue) || rateValue <= 0) {
        Swal.fire({
          icon: 'error',
          title: 'Invalid Rate',
          text: 'Exchange rate must be a positive number.'
        })
        setLoading(false)
        return
      }

      const payload = {
        current_rate: rateValue
      }

      // Use POST if rate doesn't exist, PUT if it exists
      if (rateExists) {
        await apiClient.put(`${BASE_URL}/api/sales/exchange-rate/`, payload)
        Swal.fire({
          icon: 'success',
          title: 'Success',
          text: 'Exchange rate updated successfully!',
          confirmButtonText: 'OK'
        })
      } else {
        await apiClient.post(`${BASE_URL}/api/sales/exchange-rate/`, payload)
        setRateExists(true)
        Swal.fire({
          icon: 'success',
          title: 'Success',
          text: 'Exchange rate created successfully!',
          confirmButtonText: 'OK'
        })
      }
    } catch (error) {
      console.error('Error saving exchange rate:', error)
      const errorMessage = error.response?.data?.current_rate?.[0] ||
                          error.response?.data?.detail ||
                          error.response?.data?.message ||
                          'Failed to save exchange rate. Please try again.'
      
      // If PUT fails with "does not exist" error, try POST instead
      if (errorMessage && errorMessage.includes('Use POST method to create it')) {
        try {
          await apiClient.post(`${BASE_URL}/api/sales/exchange-rate/`, {
            current_rate: parseFloat(currentRate)
          })
          setRateExists(true)
          Swal.fire({
            icon: 'success',
            title: 'Success',
            text: 'Exchange rate created successfully!',
            confirmButtonText: 'OK'
          })
        } catch (postError) {
          const postErrorMessage = postError.response?.data?.current_rate?.[0] ||
                                  postError.response?.data?.detail ||
                                  postError.response?.data?.message ||
                                  'Failed to create exchange rate. Please try again.'
          
          Swal.fire({
            icon: 'error',
            title: 'Error',
            text: postErrorMessage
          })
        }
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: errorMessage
        })
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <div className="pagetitle">
        <h1>Exchange Rate</h1>
        <nav>
          <ol className="breadcrumb">
            <li className="breadcrumb-item"><a href="/">Home</a></li>
            <li className="breadcrumb-item">Sales</li>
            <li className="breadcrumb-item active">Exchange Rate</li>
          </ol>
        </nav>
      </div>

      <section className="section">
        <div className="row">
          <div className="col-lg-12">
            <div className="card">
              <div className="card-body">
                <h5 className="card-title">
                  <i className="bi bi-currency-exchange me-2"></i>
                  Exchange Rate (USD to Zig)
                </h5>

                {fetching ? (
                  <div className="text-center py-4">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit}>
                    <div className="row mb-3">
                      <label htmlFor="current_rate" className="col-sm-2 col-form-label">
                        Current Rate <span className="text-danger">*</span>
                        <small className="text-muted d-block">(1 USD = ? Zig)</small>
                      </label>
                      <div className="col-sm-10">
                        <input
                          type="number"
                          className="form-control"
                          id="current_rate"
                          value={currentRate}
                          onChange={(e) => setCurrentRate(e.target.value)}
                          required
                          min="0"
                          step="0.000001"
                          placeholder="Enter exchange rate"
                          disabled={loading}
                        />
                        <small className="text-muted">Enter how many Zig equal 1 USD</small>
                      </div>
                    </div>

                    <div className="row mb-3">
                      <div className="col-sm-10 offset-sm-2">
                        <button 
                          type="submit" 
                          className="btn btn-primary"
                          disabled={loading}
                        >
                          {loading ? (
                            <>
                              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                              {rateExists ? 'Updating...' : 'Creating...'}
                            </>
                          ) : (
                            <>
                              <i className="bi bi-check-circle me-2"></i>
                              {rateExists ? 'Update Exchange Rate' : 'Create Exchange Rate'}
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  )
}

export default ExchangeRate
